/**
 * Notification Bell Component
 * 
 * Displays a bell icon with an unread notification count badge.
 * Clicking opens a dropdown showing recent notifications.
 * 
 * Loads notifications from Supabase for persistence across sessions.
 * 
 * Usage:
 * ```tsx
 * <NotificationBell />
 * ```
 */

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import type { MouseEvent } from 'react';
import { Bell, X, Clock, AlertTriangle, Info, CheckCircle, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDismissedTaskNotificationIds } from '../../hooks/useDismissedTaskNotificationIds';
import { useUIStore } from '../../stores/prioritization/uiStore';
import { usePrioritizationTaskStore } from '../../stores/prioritization/prioritizationTaskStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import {
  loadNotificationsForUser,
  markNotificationReadInSupabase,
  markNotificationsSeenInSupabase,
  dismissNotificationInSupabase,
  type StoredNotification
} from '../../lib/notificationService';
import {
  countActionableUnread,
  isActionableUnread,
  isTaskPendingNotification,
  isUiNotificationBadgeEligible,
  shouldShowNotificationPing,
  type NotificationPriority,
} from '../../lib/notificationBadgeRules';
import { useSessionStartingSoonNotifications } from '../../hooks/useSessionStartingSoonNotifications';
import { useSessionNotificationJoinability } from '../../hooks/useSessionNotificationJoinability';
import {
  resolveSessionNotificationAffordance,
  sessionIdFromNotification,
} from '../../lib/sessionNotificationAffordance';
import { monoIconButton } from '@/components/mono/iconButton';

// ============================================================================
// Types
// ============================================================================

/// The panel shows this many rows; only rows that were on screen are marked seen.
const MAX_VISIBLE = 10;

interface NotificationItem {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'task';
  title: string;
  message: string;
  timestamp: Date;
  read?: boolean;
  priority?: NotificationPriority;
  actionUrl?: string;
  actionLabel?: string;
  /** Notification event slug — needed to tell a "Live now" row from any other. */
  event?: string;
  /** Session the row points at, when it points at one. */
  sessionId?: string;
}

// ============================================================================
// Component
// ============================================================================

export function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [supabaseNotifications, setSupabaseNotifications] = useState<StoredNotification[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // GAP-4 leftover: T-15 starting-soon while the app is open
  useSessionStartingSoonNotifications();
  
  // Get notifications from UI store (for real-time toasts)
  const uiNotifications = useUIStore(state => state.notifications);
  const removeNotification = useUIStore(state => state.removeNotification);
  const clearNotifications = useUIStore(state => state.clearNotifications);
  const addNotification = useUIStore(state => state.addNotification);
  
  // Get pending tasks for the current user
  const userId = useUserStore(state => state.userId);
  const { dismissedTaskIds, dismissTaskNotification, dismissAllTaskNotifications } =
    useDismissedTaskNotificationIds(userId);
  const allTasks = usePrioritizationTaskStore(state => state.tasks);
  const loadTasksFromSupabase = usePrioritizationTaskStore(state => state.loadTasksFromSupabase);
  
  // Load notifications from Supabase
  const loadNotifications = useCallback(async (options?: { markAttended?: boolean }) => {
    if (!userId) return;
    setIsLoading(true);
    try {
      const notifications = await loadNotificationsForUser(userId);
      if (options?.markAttended) {
        // "Read" means the row was on screen. The panel shows the newest
        // MAX_VISIBLE rows, so those are the ones marked seen — never every
        // unread row the person has (PRD notify-and-act, FR-0f).
        const shown = notifications.slice(0, MAX_VISIBLE);
        const unreadShownIds = shown.filter((n) => !n.read).map((n) => n.id);
        const updated = unreadShownIds.length > 0 ? await markNotificationsSeenInSupabase(unreadShownIds) : 0;
        if (unreadShownIds.length > 0 && updated === null) {
          setSupabaseNotifications(notifications);
          addNotification({
            type: 'error',
            title: 'Could not mark notifications read',
            message: 'Try again.',
            duration: 5000,
          });
        } else {
          const attendedAt = new Date();
          const seen = new Set(unreadShownIds);
          setSupabaseNotifications(
            notifications.map((n) =>
              seen.has(n.id) ? { ...n, read: true, readAt: attendedAt } : n,
            ),
          );
          clearNotifications();
        }
      } else {
        setSupabaseNotifications(notifications);
      }
    } catch (err) {
      console.error('[NotificationBell] Error loading notifications:', err);
    } finally {
      setIsLoading(false);
    }
  }, [userId, clearNotifications, addNotification]);

  // Load on mount and when user changes
  useEffect(() => {
    loadNotifications();
    loadTasksFromSupabase();
  }, [loadNotifications, loadTasksFromSupabase]);

  // Poll every 30 s so participants see newly assigned tasks/notifications
  // without needing to manually open the bell or refresh the page.
  useEffect(() => {
    if (!userId) return;
    const id = setInterval(() => {
      loadNotifications();
      loadTasksFromSupabase();
    }, 30_000);
    return () => clearInterval(id);
  }, [userId, loadNotifications, loadTasksFromSupabase]);
  
  // Reload when dropdown opens; opening the panel counts as attended (mark read + clear toasts).
  useEffect(() => {
    if (isOpen) {
      void loadNotifications({ markAttended: true });
      loadTasksFromSupabase();
    }
  }, [isOpen, loadNotifications, loadTasksFromSupabase]);
  
  const pendingTasks = useMemo(() => {
    if (!userId) return [];
    return allTasks.filter((t) => isTaskPendingNotification(t, userId, dismissedTaskIds));
  }, [allTasks, userId, dismissedTaskIds]);
  
  // Combine notifications from all sources
  const allNotifications = useMemo(() => {
    const items: NotificationItem[] = [];
    const seenIds = new Set<string>();
    
    // Add Supabase notifications first (persistent)
    supabaseNotifications.forEach(n => {
      if (seenIds.has(n.id)) return;
      seenIds.add(n.id);
      items.push({
        id: n.id,
        type: n.payload.event?.includes('task') ? 'task' : 'info',
        title: n.payload.title,
        message: n.payload.body,
        timestamp: n.createdAt,
        read: n.read,
        priority: n.payload.priority,
        actionUrl: n.payload.actionUrl,
        actionLabel: n.payload.actionLabel,
        event: n.payload.event,
        sessionId:
          sessionIdFromNotification({
            data: n.payload.data as Record<string, unknown> | null | undefined,
            actionUrl: n.payload.actionUrl,
          }) ?? undefined,
      });
    });
    
    // Add UI notifications (transient, for this session)
    uiNotifications.forEach(n => {
      if (seenIds.has(n.id)) return;
      seenIds.add(n.id);
      const eligible = isUiNotificationBadgeEligible(n.timestamp);
      items.push({
        id: n.id,
        type: n.type,
        title: n.title,
        message: n.message,
        timestamp: n.timestamp,
        read: !eligible,
        priority: 'normal',
      });
    });
    
    // A task is listed once: by the task_assigned row the database holds
    // (subject 'task'). It is finished when the task completes (migration 1053).

    // Sort by timestamp descending
    return items.sort((a, b) => 
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [supabaseNotifications, uiNotifications]);
  
  // "Live now" is the product's only urgent alert; resolve what each one points
  // at so a closed room stops advertising a Join button.
  const sessionJoinability = useSessionNotificationJoinability(supabaseNotifications);

  const unreadCount = countActionableUnread(allNotifications);
  const showPing = shouldShowNotificationPing(allNotifications);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);
  
  const handleDismiss = useCallback(
    async (id: string, e?: MouseEvent) => {
      e?.stopPropagation();
      e?.preventDefault();

      if (id.startsWith('task-')) {
        dismissTaskNotification(id);
        return;
      }

      const supabaseNotif = supabaseNotifications.find((n) => n.id === id);
      if (supabaseNotif) {
        // Optimistic remove; put it back if the server refuses, so a failed
        // dismiss never looks like it worked.
        setSupabaseNotifications((prev) => prev.filter((n) => n.id !== id));
        const ok = await dismissNotificationInSupabase(id).catch((err) => {
          console.warn('[NotificationBell] dismissNotificationInSupabase:', err);
          return false;
        });
        if (!ok) {
          setSupabaseNotifications((prev) =>
            prev.some((n) => n.id === id) ? prev : [supabaseNotif, ...prev],
          );
          addNotification({
            type: 'error',
            title: 'Notification was not dismissed',
            message: 'The server refused the change. Try again.',
            duration: 6000,
          });
          return;
        }
      }
      removeNotification(id);
    },
    [dismissTaskNotification, removeNotification, supabaseNotifications, addNotification]
  );
  
  const handleMarkAsRead = async (id: string) => {
    const supabaseNotif = supabaseNotifications.find(n => n.id === id);
    if (supabaseNotif && !supabaseNotif.read) {
      // Optimistic, then roll back on refusal.
      setSupabaseNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, read: true, readAt: new Date() } : n)
      );
      const ok = await markNotificationReadInSupabase(id).catch(() => false);
      if (!ok) {
        setSupabaseNotifications(prev =>
          prev.map(n => n.id === id ? { ...n, read: supabaseNotif.read, readAt: supabaseNotif.readAt } : n)
        );
        addNotification({
          type: 'error',
          title: 'Notification was not marked read',
          message: 'The server refused the change. Try again.',
          duration: 6000,
        });
      }
    }
  };
  
  const getIcon = (type: NotificationItem['type']) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-[var(--green)]" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-[var(--mustard)]" />;
      case 'error':
        return <AlertTriangle className="h-4 w-4 text-[var(--coral)]" />;
      case 'task':
        return <Clock className="h-4 w-4 text-[var(--text-ter)]" />;
      default:
        return <Info className="h-4 w-4 text-[var(--text-ter)]" />;
    }
  };
  
  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - new Date(date).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const handleClearAll = useCallback(async () => {
    const ids = supabaseNotifications.map((n) => n.id);
    const results = await Promise.all(
      ids.map(async (id) => ({ id, ok: await dismissNotificationInSupabase(id).catch(() => false) })),
    );
    const failedIds = new Set(results.filter((r) => !r.ok).map((r) => r.id));
    clearNotifications();
    dismissAllTaskNotifications(pendingTasks.map((t) => t.id));
    // Only drop the ones that actually dismissed — a row that was refused
    // stays visible instead of vanishing as if it had cleared.
    setSupabaseNotifications((prev) => prev.filter((n) => failedIds.has(n.id)));
    if (failedIds.size > 0) {
      addNotification({
        type: 'error',
        title: 'Some notifications were not cleared',
        message: `${failedIds.size} of ${ids.length} could not be dismissed. Try again.`,
        duration: 6000,
      });
    }
  }, [
    supabaseNotifications,
    clearNotifications,
    dismissAllTaskNotifications,
    addNotification,
    pendingTasks,
  ]);
  
  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={monoIconButton('sm', 'relative')}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Bell className="h-4 w-4 shrink-0" />
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-5 w-5 items-center justify-center">
            {showPing && (
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--coral)] opacity-75" />
            )}
            <span className="relative inline-flex h-4 w-4 items-center justify-center rounded-full bg-[var(--coral)] text-[10px] font-bold text-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          </span>
        )}
      </button>
      
      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-[var(--bg-raised)] shadow-lg border border-[var(--rule)] z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-[var(--surface)]">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-[var(--text-pri)]">Notifications</h3>
              {isLoading && (
                <Loader2 className="h-4 w-4 text-[var(--text-ter)] animate-spin" />
              )}
            </div>
            {allNotifications.length > 0 && (
              <button
                type="button"
                onClick={() => void handleClearAll()}
                className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]"
              >
                Clear all
              </button>
            )}
          </div>
          
          {/* Notification List */}
          <div className="max-h-96 overflow-y-auto">
            {allNotifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-[var(--text-ter)]">
                <Bell className="h-8 w-8 mx-auto mb-2 text-[var(--text-ter)]" />
                <p className="text-sm">No notifications</p>
              </div>
            ) : (
              <div className="divide-y">
                {allNotifications.slice(0, MAX_VISIBLE).map(notification => (
                  <div
                    key={notification.id}
                    className={`px-4 py-3 hover:bg-[var(--surface)] transition-colors ${
                      isActionableUnread(notification) ? 'bg-[var(--slate-dim)]/50' : ''
                    }`}
                  >
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {getIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="flex items-center gap-1.5 text-sm font-medium text-[var(--text-pri)]">
                          <span className="truncate">{notification.title}</span>
                          {notification.priority === 'urgent' && (
                            <span className="shrink-0 rounded-sm bg-[var(--coral-dim)] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[var(--coral)]">
                              Urgent
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-[var(--text-sec)] line-clamp-2">
                          {notification.message}
                        </p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-xs text-[var(--text-ter)]">
                            {formatTime(notification.timestamp)}
                          </span>
                          {(() => {
                            const affordance = resolveSessionNotificationAffordance({
                              event: notification.event,
                              actionUrl: notification.actionUrl,
                              actionLabel: notification.actionLabel,
                              sessionId: notification.sessionId,
                              joinability: notification.sessionId
                                ? sessionJoinability[notification.sessionId]
                                : undefined,
                            });
                            if (!affordance) return null;
                            return (
                              <>
                                {affordance.statusChip && (
                                  <span className="rounded-sm bg-[var(--surface)] px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[var(--text-ter)]">
                                    {affordance.statusChip}
                                  </span>
                                )}
                                <Link
                                  to={affordance.actionUrl}
                                  onClick={() => {
                                    handleMarkAsRead(notification.id);
                                    setIsOpen(false);
                                  }}
                                  className="text-xs text-primary-600 hover:text-primary-700"
                                >
                                  {affordance.actionLabel}
                                </Link>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(ev) => void handleDismiss(notification.id, ev)}
                        className="flex-shrink-0 p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded"
                        aria-label="Dismiss notification"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Footer */}
          {allNotifications.length > 10 && (
            <div className="px-4 py-2 border-t bg-[var(--surface)]">
              <Link
                to="/prioritization/notifications"
                onClick={() => setIsOpen(false)}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                View all notifications
              </Link>
            </div>
          )}
          
          {/* Quick link to My Tasks */}
          {pendingTasks.length > 0 && (
            <div className="px-4 py-2 border-t bg-[var(--slate-dim)]">
              <Link
                to="/prioritization/my-work"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-2 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)]"
              >
                <Clock className="h-4 w-4" />
                You have {pendingTasks.length} pending task{pendingTasks.length !== 1 ? 's' : ''}
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
